import React, { useRef, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { generatePath } from 'react-router';
import history from '@AppRoutes/history';
import { paths } from '@AppRoutes/constants';
import Auth from '@Services/Auth';
import AppStorage from '@Services/AppStorage';
import PropTypes from 'prop-types';
import { Modal } from '@els/els-react--modal';
import { ButtonWithIcon } from '@els/els-react--button';
import { TextInput } from '@els/els-react--text-input';
import { Loader } from '@els/els-react--loader';
import { historyReducerKey } from '../Constants';
import { handleNameChange, onSubmitForm, closeSaveModal } from '../HistoryAction';
import ConfirmationPopup from '../ConfirmationPopup';
import { trackAdobeAnalyticsError, trackConversionDriverClick } from '../analytics';

import './SaveModal.scss';

function SaveModal({ closed, selfClose }) {
  const {
    itemToBeSavedContext,
    saveModalTitle,
    saveModalNameInput,
    isInputTouched,
    itemToBeSaved,
    isSaveInProgress,
    error,
    showSaveModal,
  } = useSelector((state) => state.get(historyReducerKey));
  const [isModalOpen, setIsModalOpen] = useState(true);
  const dispatch = useDispatch();
  const inputRef = useRef();
  const trimmedName = saveModalNameInput.trim();
  const errorTitle = 'Login required';
  const errorMessage = 'Please sign-in/register to save your History.';
  useEffect(() => {
    if (inputRef && inputRef.current && showSaveModal) {
      inputRef.current.focus();
    }
  }, [showSaveModal]);

  useEffect(() => {
    if (!showSaveModal) {
      setIsModalOpen(false);
    }
  }, [showSaveModal]);

  useEffect(() => {
    if (closed) {
      setIsModalOpen(false);
      dispatch(closeSaveModal());
    }
  }, [closed]);

  const isSaveDisabled = () => isSaveInProgress || trimmedName === '' || trimmedName.length < 3 || trimmedName.length > 50;
  const handleSubmit = (event) => {
    event.preventDefault();
    dispatch(onSubmitForm(itemToBeSaved, trimmedName, saveModalTitle === 'Rename'));
  };
  const resolveFormError = () => {
    if (!trimmedName) {
      return '';
    }
    if (isInputTouched && !trimmedName) {
      return 'Name is required.';
    }
    if (trimmedName.length < 3) {
      return 'Name is too short, minimum length is 3 characters.';
    }
    if (trimmedName.length > 50) {
      return 'Name is too long, maximum length is 50 characters.';
    }
    return '';
  };

  const trackError = () => {
    const formError = resolveFormError();
    trackAdobeAnalyticsError(formError);
  };

  const signIn = () => {
    history.push({
      pathname: generatePath(paths.LOGIN),
    });
  };

  const handleConirmSignIn = (handleSignIn) => {
    dispatch(closeSaveModal());
    handleSignIn();
  };

  if (Auth.isAnonymousUser()) {
    return (
      <ConfirmationPopup
        title={errorTitle}
        text={errorMessage}
        submitButtonText="Sign in"
        dismissButtonText="Cancel"
        isCancelButton
        onClose={() => {
          dispatch(closeSaveModal());
        }}
        onConfirm={() => {
          trackConversionDriverClick('history:savesearch:sign-in');
          AppStorage.instance.set('scheduledHistory', itemToBeSaved);
          handleConirmSignIn(signIn);
        }}
        closed={closed}
        selfClose={selfClose}
      />
    );
  }
  return (
    isModalOpen && (
      <Modal
        onClose={() => {
          setIsModalOpen(false);
          dispatch(closeSaveModal());
        }}>
        <header>
          <h4 className="u-els-padding-left-1x">{`${saveModalTitle} ${itemToBeSavedContext.replace(/"/g, "'")}`}</h4>
        </header>

        <form onSubmit={handleSubmit}>
          <Loader id="history-save-loading" isVisible={isSaveInProgress} />
          <section className="modal-content">
            <div className="u-els-padding-1x  u-els-padding-bottom-2x">
              <TextInput
                id="searchName"
                name="searchName"
                className="u-els-padding-top-2x  u-els-padding-bottom-1o8"
                value={saveModalNameInput || ''}
                onChange={(e) => dispatch(handleNameChange(e?.target?.value))}
                required
                ref={inputRef}
                label="Name of result set"
                key="searchNameKey"
                onBlur={() => {
                  trackError();
                }}
              />
              {error && error.message && <span className="u-els-color-warn">Error saving this search</span>}
              {trimmedName.length < 3 && trimmedName.length > 0 && (
                <span className="u-els-color-warn">The name is too short, minimum length is 3 characters.</span>
              )}
              {trimmedName.length > 50 && <span className="u-els-color-warn">The name is too long, maximum length is 50 characters.</span>}
              {isInputTouched && trimmedName === '' && <span className="u-els-color-warn">The name is required.</span>}
            </div>
          </section>
          <footer>
            <span className="button-container">
              <ButtonWithIcon
                className="e2e-save-modal-cancel"
                type="secondary"
                iconRight
                sprite="Close"
                onClick={() => {
                  setIsModalOpen(false);
                  dispatch(closeSaveModal());
                }}>
                Cancel
              </ButtonWithIcon>
              <ButtonWithIcon
                className="e2e-save-modal-save"
                sprite="ChevronRight"
                iconRight
                htmlType="submit"
                disabled={isSaveDisabled()}
                onClick={(event) => {
                  handleSubmit(event);
                }}>
                Save
              </ButtonWithIcon>
            </span>
          </footer>
        </form>
      </Modal>
    )
  );
}

SaveModal.propTypes = {
  selfClose: PropTypes.func,
  closed: PropTypes.bool,
};

export default SaveModal;
